import type { ComponentProps } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

const COLOR_CLASSES = [
  'bg-blue-500/12 text-blue-700 dark:text-blue-300',
  'bg-emerald-500/12 text-emerald-700 dark:text-emerald-300',
  'bg-amber-500/12 text-amber-700 dark:text-amber-300',
  'bg-rose-500/12 text-rose-700 dark:text-rose-300',
  'bg-violet-500/12 text-violet-700 dark:text-violet-300',
  'bg-cyan-500/12 text-cyan-700 dark:text-cyan-300',
  'bg-orange-500/12 text-orange-700 dark:text-orange-300',
  'bg-fuchsia-500/12 text-fuchsia-700 dark:text-fuchsia-300',
];

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function bucketColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return COLOR_CLASSES[h % COLOR_CLASSES.length];
}

type Props = ComponentProps<typeof Avatar> & {
  name: string;
  src?: string | null;
};

/**
 * Avatar for an entity (client, contact, project) keyed on its name. Generates
 * deterministic initials + a color from one of eight muted tints — gives every
 * row a hint of identity without anyone having uploaded a photo.
 */
export function EntityAvatar({ name, src, className, size = 'default', ...rest }: Props) {
  return (
    <Avatar size={size} className={cn(className)} {...rest}>
      {src ? <AvatarImage src={src} alt={name} /> : null}
      <AvatarFallback className={cn('font-medium', bucketColor(name))}>
        {initialsFor(name)}
      </AvatarFallback>
    </Avatar>
  );
}
